import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import type { ComponentType } from "react";
import TextJs from "components/common/Text";
import SelectJs from "components/common/Select";
import AutoCompleteJs from "components/common/AutoComplete";
import PhoneInputJs from "components/common/PhoneInput";
import ToolPicker from "plg/screens/Signup/ToolPicker";
import usePlgToolCatalogue from "plg/screens/Signup/usePlgToolCatalogue";

import { allConfigTools, isFieldMissing, resolveOptions, suggestedTools } from "plg/utils/configSchema";
import TileChoice from "plg/screens/Signup/TileChoice";
import { cleanTools, isProTool, toolLogos } from "components/Screens/Marketplace/Customer/utils/tools";
import { formatUsPhone, US_ONLY } from "plg/utils/usPhone";
import type { PlgField, PlgSignupConfig } from "plg/types";

// The common inputs are untyped JSX whose inferred props make every prop required;
// they all read react-hook-form context, so only name/label/etc. are needed here.
type LooseInput = ComponentType<Record<string, unknown>>;
const Text = TextJs as unknown as LooseInput;
const Select = SelectJs as unknown as LooseInput;
const AutoComplete = AutoCompleteJs as unknown as LooseInput;

/** Touch screens (phones, tablets): checked once; a device does not change pointer type mid-session. */
const IS_TOUCH = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

/** A one-pick list longer than this (e.g. US states) gets a search box instead of a plain dropdown. */
const SEARCHABLE_OVER = 15;
const PhoneInput = PhoneInputJs as unknown as LooseInput;

export function groupFieldsIntoRows(fields: PlgField[]): PlgField[][] {
  const rows: PlgField[][] = [];
  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    const next = fields[i + 1];
    if (field.width === "half" && next?.width === "half") {
      rows.push([field, next]);
      i += 1;
    } else {
      rows.push([field]);
    }
  }
  return rows;
}

interface FieldRendererProps {
  field: PlgField;
  config: PlgSignupConfig;
  enriched: boolean;
  /** Current industry answer, for toolPicker suggestions. */
  industry?: string;
  /** Typeahead source override (Business profile passes the full catalogue). */
  toolOptions?: string[];
  /** false: no "Optional" tag on this field's label (step hideOptionalTags). */
  optionalTag?: boolean;
}

// Catalogue plus config tools, so config names stay searchable whatever the marketplace returns. One
// entry per tool and professional software only (prototype TOOL_LABEL / isPro; QA ON-4).
const mergeTools = (catalogue: string[] | null, configTools: string[]) =>
  cleanTools([...configTools, ...(catalogue || [])]).sort((a, b) => a.localeCompare(b));

const looseKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * "Common tools for your industry": the configured tools that talent can actually be matched on, i.e. that
 * are in the marketplace catalogue, under its spelling (QA ON-5). Without the catalogue, the config list.
 */
function industrySuggestions(config: PlgSignupConfig, industry: string | undefined, catalogue: string[] | null): string[] {
  const configured = suggestedTools(config, industry).filter(isProTool);
  if (!catalogue) return configured;
  const byKey = new Map(cleanTools(catalogue).map((t) => [looseKey(t), t]));
  return [...new Set(configured.map((t) => byKey.get(looseKey(t))).filter((t): t is string => !!t))];
}

function FieldRenderer({ field, config, enriched, industry, toolOptions, optionalTag = true }: FieldRendererProps) {
  const { control } = useFormContext();
  const value = useWatch({ control, name: field.key });
  // The answer that reveals this field (field.showWhen); the field's own value when it has none.
  const controlling = useWatch({ control, name: field.showWhen?.field || field.key });
  const catalogueTools = usePlgToolCatalogue();
  const catalogue = useMemo(() => catalogueTools?.map((t) => t.name) ?? null, [catalogueTools]);
  const logos = useMemo(() => toolLogos(catalogueTools || []), [catalogueTools]);
  const missing = isFieldMissing(field, { [field.key]: value }, enriched);
  // The prototype's "Optional" tag (.f-opt) is drawn by CSS next to the label, so the shared inputs never
  // add their own "(optional)" text.
  const isOptional = false;
  const label = field.label || "";

  if (field.showWhen && controlling !== field.showWhen.equals) return null;

  const body = (() => {
    if (field.type === "select" && field.display === "tiles") {
      return (
        <Controller
          name={field.key}
          control={control}
          render={({ field: rhf }) => (
            <TileChoice label={label} options={resolveOptions(field, config)} value={rhf.value || ""} onChange={rhf.onChange} />
          )}
        />
      );
    }
    const options = resolveOptions(field, config);
    if (field.type === "select" && options.length > SEARCHABLE_OVER) {
      // Type to find it (product, 2026-10-23: State on step 1).
      return (
        <AutoComplete
          name={field.key}
          label={label}
          placeholder={field.placeholder || "Select"}
          options={options}
          isOptional={isOptional}
          // Looks like the other dropdowns: the chevron stays, no clear (x) button over it.
          disableClearable
          // On phones a tap selected the chosen name and iOS showed its text-selection handles; the full list still
          // opens on tap, and the chosen name is not filtering it.
          selectOnFocus={!IS_TOUCH}
        />
      );
    }
    switch (field.type) {
      case "select":
      case "multiSelect":
        return (
          <Select
            name={field.key}
            label={label}
            placeholder={field.placeholder || "Select"}
            options={options}
            multiple={field.type === "multiSelect"}
            isOptional={isOptional}
            typeAhead
            menuBelow
          />
        );
      case "phone":
        return (
          <PhoneInput
            name={field.key}
            label={label}
            fieldPlaceholder={field.placeholder}
            isOptional={isOptional}
            defaultCountry="United States"
            countries={US_ONLY}
            formatDisplay={formatUsPhone}
          />
        );
      case "textarea":
        return <Text name={field.key} label={label} placeholder={field.placeholder} isOptional={isOptional} multiline rows={3} />;
      case "toolPicker":
        return (
          <Controller
            name={field.key}
            control={control}
            render={({ field: rhf }) => (
              <ToolPicker
                value={rhf.value || []}
                onChange={rhf.onChange}
                suggestions={industrySuggestions(config, industry, catalogue)}
                options={toolOptions || mergeTools(catalogue, allConfigTools(config))}
                logos={logos}
                searchPlaceholder={field.searchPlaceholder}
                label={field.label}
              />
            )}
          />
        );
      case "text":
      default:
        return <Text name={field.key} label={label} placeholder={field.placeholder} isOptional={isOptional} />;
    }
  })();

  return (
    <div className={`plg-field${missing ? " plg-field--missing" : ""}${field.required || !optionalTag ? "" : " plg-field--optional"}`}>
      {body}
      {field.hint && <div className="plg-field__hint">{field.hint}</div>}
    </div>
  );
}

export default FieldRenderer;
